// Apolo IDE — SidePanel: Files tab.
//
// Hotfix v1.5.5: real implementation replacing the Sprint v1 placeholder.
// Lists files from the active session's worktree via GET
// /api/code-ide/sessions/:id/files. Modified files float to the top with
// a yellow dot marker.

import { useEffect, useState } from 'react';
import { File, FileCode2, FileText, Folder, Image as ImageIcon, Loader2, RefreshCw, UploadCloud } from 'lucide-react';
import { apiFetch } from '../../../lib/api.js';
import { useAgentStore } from '../../../store/useAgentStore.js';
import { sessionRoot } from '../../../lib/sessionRoot.js';
import { rebuildSemanticIndex } from '../../../lib/semanticIndexClient.js';

const ICON_BY_EXT = {
  js: FileCode2, mjs: FileCode2, cjs: FileCode2, jsx: FileCode2,
  ts: FileCode2, tsx: FileCode2,
  py: FileCode2, rb: FileCode2, go: FileCode2, rs: FileCode2,
  json: FileCode2, xml: FileCode2, yml: FileCode2, yaml: FileCode2, toml: FileCode2,
  html: FileCode2, css: FileCode2, scss: FileCode2,
  md: FileText, txt: FileText,
  png: ImageIcon, jpg: ImageIcon, jpeg: ImageIcon, gif: ImageIcon, webp: ImageIcon, svg: ImageIcon,
};

function iconFor(filename) {
  if (filename.endsWith('/')) return Folder;
  const ext = filename.split('.').pop().toLowerCase();
  return ICON_BY_EXT[ext] || File;
}

export default function FilesTab({ sessionId, sessionDirectory }) {
  const activeSessionId = useAgentStore((s) => s.code.activeSessionId);
  const sessions = useAgentStore((s) => s.code.sessions);
  const fallbackSessionId = sessionId || activeSessionId;
  const fallbackSession = sessions.find((s) => s.id === fallbackSessionId) || null;
  const effectiveSessionId = fallbackSessionId || null;
  const effectiveRoot = sessionDirectory || sessionRoot(fallbackSession) || '';
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const [indexing, setIndexing] = useState(false);
  const [indexStatus, setIndexStatus] = useState(null);

  async function refresh() {
    if (!effectiveSessionId) return;
    setLoading(true);
    setError(null);
    const res = await apiFetch(`/api/code-ide/sessions/${effectiveSessionId}/files`);
    setLoading(false);
    if (res.ok) {
      // Expect { files: [{ path, modified, size_bytes? }] } from backend.
      const rows = res.data?.files || [];
      setFiles(rows);
    } else {
      setError(res.error || 'Falha ao carregar arquivos');
    }
  }

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveSessionId]);

  async function handleRebuildIndex() {
    if (!effectiveSessionId || !effectiveRoot) {
      setIndexStatus({ type: 'error', text: 'Sessão sem diretório local.' });
      return;
    }
    if (!window.apolo?.file?.list || !window.apolo?.file?.read) {
      setIndexStatus({ type: 'error', text: 'Rebuild precisa rodar no app Electron.' });
      return;
    }

    setIndexing(true);
    setError(null);
    setIndexStatus({ type: 'info', text: 'Lendo arquivos locais…' });
    try {
      const res = await rebuildSemanticIndex(effectiveSessionId, effectiveRoot, {
        onProgress: ({ phase, done, total }) => {
          if (phase === 'index') {
            setIndexStatus({ type: 'info', text: `Indexando ${done}/${total} arquivos…` });
          } else {
            setIndexStatus({ type: 'info', text: `Enviando ${total} arquivos para ZeroEntropy…` });
          }
        },
      });
      if (!res.ok) {
        setIndexStatus({ type: 'error', text: res.error || 'Falha na indexação.' });
        return;
      }
      setIndexStatus({ type: 'success', text: `Indexação concluída: ${res.chunks || 0} chunks.` });
    } catch (e) {
      setIndexStatus({ type: 'error', text: e?.message || 'Falha na indexação.' });
    } finally {
      setIndexing(false);
    }
  }

  const filtered = query.trim()
    ? files.filter((f) => f.path.toLowerCase().includes(query.trim().toLowerCase()))
    : files;
  const modified = filtered.filter((f) => f.modified);
  const others = filtered.filter((f) => !f.modified);

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 border-b border-apolo-borderSubtle px-3 py-2">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar arquivo…"
          aria-label="Buscar arquivo"
          disabled={!effectiveSessionId}
          className="flex-1 rounded-md border border-apolo-borderSubtle bg-apolo-surface2 px-2 py-1 text-xs text-apolo-textPrimary placeholder:text-apolo-textTertiary focus:border-apolo-accent focus:outline-none"
        />
        <button
          type="button"
          onClick={refresh}
          aria-label="Atualizar"
          title="Atualizar lista"
          disabled={loading || !effectiveSessionId}
          className="inline-flex h-6 w-6 items-center justify-center rounded-md border border-apolo-borderSubtle bg-apolo-surface2 text-apolo-textSecondary transition hover:text-apolo-textPrimary disabled:opacity-50"
        >
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
        </button>
      </div>

      <div className="border-b border-apolo-borderSubtle px-3 py-2">
        <button
          type="button"
          onClick={handleRebuildIndex}
          disabled={indexing || !effectiveSessionId || !effectiveRoot}
          className="inline-flex w-full items-center justify-center gap-2 rounded-md border border-apolo-borderSubtle bg-apolo-surface2 px-2 py-1.5 text-xs font-medium text-apolo-textSecondary transition hover:border-apolo-borderStrong hover:text-apolo-textPrimary disabled:cursor-not-allowed disabled:opacity-50"
          title={effectiveRoot ? 'Reindexar arquivos desta sessão com ZeroEntropy (zembed-1 + zerank-2)' : 'Sessão sem diretório local'}
        >
          {indexing ? <Loader2 size={13} className="animate-spin" /> : <UploadCloud size={13} />}
          {indexing ? 'Indexando…' : 'Indexar ZeroEntropy'}
        </button>
        {indexStatus && (
          <p
            className={`mt-1 text-[11px] ${
              indexStatus.type === 'error'
                ? 'text-apolo-error'
                : indexStatus.type === 'success'
                  ? 'text-apolo-success'
                  : 'text-apolo-textTertiary'
            }`}
          >
            {indexStatus.text}
          </p>
        )}
      </div>

      {error && (
        <div role="alert" className="border-b border-apolo-error/30 bg-apolo-error/10 px-3 py-1.5 text-xs text-apolo-error">
          {error}
        </div>
      )}

      {!effectiveSessionId && (
        <div className="border-b border-apolo-borderSubtle px-3 py-2 text-xs text-apolo-textTertiary">
          Abra uma sessão Code para habilitar a indexação.
        </div>
      )}

      {effectiveSessionId && !effectiveRoot && (
        <div className="border-b border-apolo-borderSubtle px-3 py-2 text-xs text-apolo-warning">
          Esta sessão não tem diretório local configurado.
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-2 py-1">
        {!loading && filtered.length === 0 && !error && (
          <div className="flex h-full items-center justify-center text-center">
            <p className="max-w-xs text-xs text-apolo-textTertiary">
              {query ? `Nenhum arquivo casa "${query}"` : 'Nenhum arquivo na worktree.'}
            </p>
          </div>
        )}

        {modified.length > 0 && (
          <div className="mb-2">
            <h3 className="px-1 py-1 text-[10px] uppercase tracking-wider text-apolo-textTertiary">
              Modificados ({modified.length})
            </h3>
            <ul>
              {modified.map((f) => <FileRow key={f.path} file={f} highlight />)}
            </ul>
          </div>
        )}

        {others.length > 0 && (
          <div>
            {modified.length > 0 && (
              <h3 className="px-1 py-1 text-[10px] uppercase tracking-wider text-apolo-textTertiary">
                Tudo ({others.length})
              </h3>
            )}
            <ul>
              {others.map((f) => <FileRow key={f.path} file={f} />)}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function FileRow({ file, highlight }) {
  const Icon = iconFor(file.path);
  return (
    <li className="group flex items-center gap-1.5 rounded-md px-1.5 py-1 text-xs hover:bg-apolo-surface2">
      <Icon size={12} aria-hidden="true" className="shrink-0 text-apolo-textTertiary" />
      <span className="truncate" title={file.path}>{file.path}</span>
      {highlight && (
        <span
          aria-label="Modificado"
          title="Modificado"
          className="ml-auto h-1.5 w-1.5 shrink-0 rounded-full bg-apolo-warning"
        />
      )}
      {file.size_bytes && !highlight && (
        <span className="ml-auto shrink-0 text-[10px] text-apolo-textTertiary opacity-0 group-hover:opacity-100">
          {formatBytes(file.size_bytes)}
        </span>
      )}
    </li>
  );
}

function formatBytes(n) {
  if (n < 1024) return `${n}B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)}KB`;
  return `${(n / 1024 / 1024).toFixed(1)}MB`;
}
